export function calculateMode(numbers: number[]): number[] {
  const frequencyMap: Map<number, number> = new Map();
  let maxFrequency = 0;
  let modes: number[] = [];

  for (const num of numbers) {
    const frequency = (frequencyMap.get(num) || 0) + 1;
    frequencyMap.set(num, frequency);

    if (frequency > maxFrequency) {
      maxFrequency = frequency;
      modes = [num];
    } else if (frequency === maxFrequency) {
      modes.push(num);
    }
  }

  return modes;
}

export function calculateMean(numbers: number[]): number {
  const sum = numbers.reduce(
    (accumulator, current) => accumulator + current,
    0
  );
  return sum / numbers.length;
}

export function calculateMedian(numbers: number[]): number {
  const sortedNumbers = [...numbers].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedNumbers.length / 2);

  if (sortedNumbers.length % 2 === 0) {
    // Even number of elements, return the average of middle two elements
    return (sortedNumbers[middleIndex - 1] + sortedNumbers[middleIndex]) / 2;
  } else {
    // Odd number of elements, return the middle element
    return sortedNumbers[middleIndex];
  }
}

/**
 * Apply a moving average smoothing to an array of numbers
 * @param data Array of numerical values to smooth
 * @param windowSize Size of the smoothing window (total values used = 2*windowSize + 1)
 * @returns Smoothed array of the same length as the input
 */
export function smoothData(data: number[], windowSize: number = 3): number[] {
  // Validate inputs
  if (!data || data.length === 0) {
    return [];
  }

  // Ensure windowSize is a positive integer
  windowSize = Math.max(1, Math.floor(windowSize));

  // If data is too small for the window, return the original data
  if (data.length <= windowSize * 2) {
    return [...data];
  }

  const result = [...data];
  const windowTotal = 2 * windowSize + 1;

  // Process each point that needs smoothing (excluding edges)
  for (let i = windowSize; i < data.length - windowSize; i++) {
    // For the first point in the valid range, calculate the full window sum
    if (i === windowSize) {
      let sum = 0;
      for (let j = i - windowSize; j <= i + windowSize; j++) {
        sum += data[j];
      }
      result[i] = sum / windowTotal;
    } else {
      // For subsequent points, update the sum by removing the leftmost value
      // and adding the rightmost value of the new window
      const prevSum = result[i - 1] * windowTotal;
      const newSum = prevSum - data[i - windowSize - 1] + data[i + windowSize];
      result[i] = newSum / windowTotal;
    }
  }

  return result;
}
